import axios from 'axios';
import { API } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Interfaces
export interface InnoflexPayload {
  Phase_Start_Date: string;
  OptionsData: [];
}

export interface scPayload {
  Project_Key: string;
  SC_Phase_Key: string;
  Phase_Start_Date: string;
}

// Custom hook for Innoflex API client
export const useInnoflexApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });
     // Add response interceptor to handle 401 errors
     instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [idToken, authData.idToken]);

  // Innoflex Phases API
  const getInnoflexPhases = async () => {
    try {
      const response = await apiClient.get('/innoflexphases');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex phases');
    }
  };

  const getInnoflexList = async (project_key: any) => {
    try {
      const response = await apiClient.get(`/innoflexphases/OptionsData/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex list');
    }
  };

  const getInnoflexQuestions = async () => {
    try {
      const response = await apiClient.get('/innoflexphases/Options');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex questions');
    }
  };

  const postInnoflex = async (payload: InnoflexPayload, Project_Key: any, Innoflex_Phase_Key: any) => {
    try {
      const response = await apiClient.post(
        `/innoflexphases/OptionsData/${Project_Key}/${Innoflex_Phase_Key}`,
        payload
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'posting Innoflex data');
    }
  };

  const getInnoflex = async (Project_Key: number, Innoflex_Phase_Key: number) => {
    try {
      const response = await apiClient.get(
        `/innoflexphases/OptionsData/${Project_Key}/${Innoflex_Phase_Key}`
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex data');
    }
  };

  // SC Phases API
  const getSCList = async (project_key: any) => {
    try {
      const response = await apiClient.get(`/scphase/OptionsData/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SC list');
    }
  };

  const getScPhases = async () => {
    try {
      const response = await apiClient.get('/scphase');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SC phases');
    }
  };

  const getScQuestions = async () => {
    try {
      const response = await apiClient.get('/innoflexphases/Options');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SC questions');
    }
  };

  const postSc = async (payload: scPayload) => {
    try {
      const response = await apiClient.post('/scphase/OptionsData', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'posting SC data');
    }
  };

  const getScData = async (Project_Key: any, SC_Phase_Key: any) => {
    try {
      const response = await apiClient.get(
        `/scphase/OptionsData/${Project_Key}/${SC_Phase_Key}`
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SC data');
    }
  };

  const getInnoflexPhaseOptions = async (innoflexPhaseKey: any) => {
    try {
      const response = await apiClient.get(
        `/innoflexphases/Options?innoflexPhaseKey=${innoflexPhaseKey}`
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex questions');
    }
  };

  const getInnoflexFile = async () => {
    try {
      const response = await apiClient.get(
        '/innoflexphases/innoflexfile'
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex questions');
    }
  };
  return {
    getInnoflexPhases,
    getInnoflexList,
    getInnoflexQuestions,
    postInnoflex,
    getInnoflex,
    getSCList,
    getScPhases,
    getScQuestions,
    postSc,
    getScData,
    getInnoflexPhaseOptions,
    getInnoflexFile,
  };
};